import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import { LocalDataTable } from '../../components/LocalDataTable';
import { ConfirmDialog } from '../../components/Feedback';
import { FormGrid, NumberField, SelectField, TextField } from '../../components/Form';
import { api, toQuery } from '../../lib/api';
import { money, monthYear, useMutation, useResource } from '../../lib/hooks';
import { useLookups } from '../../lib/lookups';
import { Roles, useAuth } from '../../app/providers';
import { PaidBadge, SectionTitle } from '../../components/PageHeader';
import { IconTrash } from '../../components/Icons';

// ---------------------------------------------------------------------------
// Fees slip (printable receipt)
// ---------------------------------------------------------------------------

/**
 * A slip is linked to its fees through FeesPayments, not through `feeses`.
 *
 * Neither the legacy app nor v2 sets Fees.FeesSlipNo when a payment is taken, so the
 * `feeses` collection is empty on every slip either has created. The legacy receipt view
 * iterates Model.FeesPayments, and so does this.
 */
type SlipStudent = { id: number; name: string; fatherName: string };

type FeesSlip = {
  id: number;
  submissionDateTime: string;
  cashierId: string;
  feesPayments?: {
    id: number;
    /** What this slip took against the fee. */
    amount: number;
    fees: {
      id: number;
      month: number | null;
      year: number | null;
      amount: number;
      feesTypeId: number;
      feesType?: { initial: string; description: string };
      /* Trimmed server-side to the payments made up to this slip, so Remaining is the
         figure as it stood on the day - the legacy repository does the same. The
         serializer replaces a cycle back to a payment already in the graph with a
         literal null, so entries can be null. */
      feesPayments?: ({ amount: number } | null)[];
      student?: SlipStudent;
      studentCourse?: {
        student?: SlipStudent;
        course?: {
          groupName?: string;
          grade?: { description: string };
          subject?: { description: string };
        };
      };
    };
  }[];
};

/** Registration fees hang off the student; monthly ones off the enrolment. */
const REGISTRATION = 1;
const MONTHLY = 2;

/**
 * The receipt a parent receives. Kept deliberately plain so it prints sensibly —
 * the old system's slip was the one screen people actually put on paper.
 */
export function FeesSlipPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const slip = useResource<FeesSlip>(id ? `/api/studentfees/slip/${id}` : null);

  // The API serializes with ReferenceHandler.IgnoreCycles, which drops a repeated
  // reference as a literal null - so both this array and each fee's own feesPayments
  // can contain null entries. Every reduce/find over them has to tolerate that.
  const payments = (slip.data?.feesPayments ?? []).filter((p) => p != null);
  const total = payments.reduce((sum, p) => sum + (p.amount ?? 0), 0);

  const first = payments.find((p) => p.fees)?.fees;
  const student = first?.studentCourse?.student ?? first?.student;

  return (
    <>
      <div className="page__header">
        <div>
          <h2 className="page__title">Fees slip FS-{id}</h2>
          <p className="page__subtitle">
            {slip.data ? new Date(slip.data.submissionDateTime).toLocaleString() : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn--primary" onClick={() => window.print()}>Print</button>
          <button type="button" className="btn" onClick={() => navigate('/fees/slips')}>Back</button>
        </div>
      </div>

      <div className="card">
        {slip.loading && <p>Loading…</p>}
        {slip.error && <p className="field__error">{slip.error}</p>}

        {slip.data && (
          <>
            {student && (
              <p style={{ marginTop: 0 }}>
                <strong>{student.name}</strong> (STD-{student.id}) · Father: {student.fatherName}
              </p>
            )}

            {/* Deliberately NOT paginated: this is a printable receipt. Splitting it
                across pages would produce an incomplete document when printed, and a slip
                only ever holds the handful of lines paid in one transaction. */}
            <div style={{ overflowX: 'auto' }}>
              <table className="dt__table">
                <thead>
                  <tr>
                    <th>Student ID</th><th>Student Name</th>
                    <th>Grade</th><th>Subject</th><th>Group</th>
                    <th>Type</th><th>Month-Year</th>
                    <th style={{ textAlign: 'right' }}>Total Amount</th>
                    <th style={{ textAlign: 'right' }}>Remaining</th>
                    <th style={{ textAlign: 'right' }}>Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => {
                    const fees = payment.fees;

                    // Legacy slips can carry a payment whose fee row is gone. Render the
                    // line as unresolved rather than letting a missing `fees` throw and
                    // blank the whole receipt.
                    if (!fees) {
                      return (
                        <tr key={payment.id}>
                          <td>—</td>
                          <td className="field__error">Fee record not found</td>
                          <td colSpan={5} />
                          <td style={{ textAlign: 'right' }} className="money">—</td>
                          <td style={{ textAlign: 'right' }} className="money">—</td>
                          <td style={{ textAlign: 'right' }} className="money">{money(payment.amount)}</td>
                        </tr>
                      );
                    }

                    const isRegistration = fees.feesTypeId === REGISTRATION;

                    // Registration rows carry no course, exactly as the legacy slip printed them.
                    const person = isRegistration ? fees.student : fees.studentCourse?.student;
                    const course = isRegistration ? undefined : fees.studentCourse?.course;

                    // Remaining as at this slip: the server trims fees.feesPayments to the
                    // payments made up to its submission time, so later ones do not count.
                    // Null entries are cycle placeholders from the serializer - skip them.
                    const paidToDate = (fees.feesPayments ?? []).reduce(
                      (sum, p) => sum + (p?.amount ?? 0),
                      0,
                    );

                    return (
                      <tr key={payment.id}>
                        <td>{person ? `STD-${person.id}` : '—'}</td>
                        <td>{person?.name ?? '—'}</td>
                        <td>{course?.grade?.description ?? ''}</td>
                        <td>{course?.subject?.description ?? ''}</td>
                        <td>{course?.groupName ?? ''}</td>
                        <td>{fees.feesType?.initial ?? (isRegistration ? 'REG' : 'MON')}</td>
                        <td>{isRegistration ? '' : monthYear(fees.month, fees.year)}</td>
                        <td style={{ textAlign: 'right' }} className="money">{money(fees.amount)}</td>
                        <td style={{ textAlign: 'right' }} className="money">{money(fees.amount - paidToDate)}</td>
                        <td style={{ textAlign: 'right' }} className="money">{money(payment.amount)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <p style={{ textAlign: 'right', fontSize: 16, marginTop: 16 }}>
              Total received: <strong className="money">{money(total)}</strong>
            </p>
          </>
        )}
      </div>
    </>
  );
}

type SlipRow = {
  id: number;
  submissionDateTime: string;
  cashierId: string;
  lineCount: number;
  total: number;
};

export function FeesSlipsPage() {
  const navigate = useNavigate();

  return (
    <ListPage<SlipRow>
      title="Fees Slips"
      subtitle="Every receipt issued, newest first."
      endpoint="/api/studentfees/slips"
      rowKey={(r) => r.id}
      emptyMessage="No fees slips yet."
      filters={[
        { key: 'id', label: 'Slip #', type: 'number' },
        { key: 'cashierId', label: 'Cashier', type: 'text' },
        { key: 'dateFrom', label: 'Submitted from', type: 'date' },
        { key: 'dateTo', label: 'Submitted to', type: 'date' },
      ]}
      columns={[
        { key: 'id', header: 'Slip', render: (r) => `FS-${r.id}`, width: '100px', nowrap: true },
        {
          key: 'when',
          header: 'Submitted',
          render: (r) => new Date(r.submissionDateTime).toLocaleString(),
        },
        // Both come from the slip's FeesPayments, aggregated in SQL. Total is the same sum
        // the legacy receipt printed in its own Total row.
        { key: 'lines', header: 'Lines', align: 'right', render: (r) => r.lineCount },
        {
          key: 'total', header: 'Total', align: 'right',
          render: (r) => <span className="money">{money(r.total)}</span>,
        },
      ]}
      rowActions={(row) => (
        <button type="button" className="btn btn--sm" onClick={() => navigate(`/fees/slip/${row.id}`)}>
          View
        </button>
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// Fee details + payment removal
// ---------------------------------------------------------------------------

type FeeDetails = {
  id: number;
  amount: number;
  paidAmount: number;
  isSubmitted: boolean;
  month: number | null;
  year: number | null;
  feesTypeId: number;
  feesType?: { description: string };
  student?: { id: number; name: string };
  studentCourse?: {
    student?: { id: number; name: string };
    course?: {
      groupName?: string;
      grade?: { description: string };
      subject?: { description: string };
    };
  };
  feesPayments?: { id: number; amount: number; createdDateTime: string; createdBy: string }[];
};

/** One labelled figure in the detail header. */
function Detail({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="field" style={{ gap: 2, marginBottom: 0 }}>
      <span className="field__label">{label}</span>
      <span style={{ fontWeight: 'var(--fw-semibold)' }}>{value}</span>
    </div>
  );
}

/**
 * One fee, everything recorded against it, and the ability to reverse an instalment.
 *
 * The header carries what the legacy Fees Details screen carried. Grade, Subject, Group and
 * Month-Year are shown for monthly fees only - a registration fee hangs off the student
 * rather than an enrolment, so it has no course to describe, and the legacy view wrapped
 * exactly those four in `@if (FeesTypeId == Monthly)`.
 *
 * Removing a payment sets IsSubmitted back to false unconditionally rather than
 * recomputing it — preserved from the legacy action.
 */
export function FeeDetailsPage() {
  const { feesId } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const { run, busy } = useMutation();

  const fees = useResource<FeeDetails>(feesId ? `/api/studentfees/${feesId}/details` : null);
  const [pendingDelete, setPendingDelete] = useState<{ id: number; amount: number } | null>(null);
  const [deletingFee, setDeletingFee] = useState(false);

  async function confirmDelete() {
    if (!pendingDelete) return;

    const ok = await run(
      () => api.delete(`/api/studentfees/payment/${pendingDelete.id}`),
      'Payment reversed.',
      () => fees.reload(),
    );

    if (ok) setPendingDelete(null);
  }

  async function confirmDeleteFee() {
    const ok = await run(
      () => api.delete(`/api/studentfees/monthly/${feesId}`),
      'Fees record deleted.',
    );

    if (ok) navigate('/fees');
  }

  const data = fees.data;
  const isMonthly = data?.feesTypeId === MONTHLY;

  // Registration fees hang off the student directly; monthly ones off the enrolment.
  const student = isMonthly ? data?.studentCourse?.student : data?.student;
  const course = data?.studentCourse?.course;

  const payments = data?.feesPayments ?? [];
  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);

  // Legacy enabled Delete only with no payments against the fee: "Only unpaid fees can be
  // deleted." Director-only there too.
  const canDeleteFee = hasRole(Roles.Director) && isMonthly && payments.length === 0;

  return (
    <>
      <div className="page__header">
        <div>
          <h2 className="page__title">Fee #{feesId}</h2>
          <p className="page__subtitle">
            {student ? `${student.name} (STD-${student.id})` : ''}
          </p>
        </div>
        <button type="button" className="btn" onClick={() => navigate('/fees')}>Back</button>
      </div>

      <div className="card">
        {fees.loading && <p>Loading…</p>}
        {fees.error && <p className="field__error">{fees.error}</p>}

        {data && (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 'var(--sp-4) var(--sp-5)',
              }}
            >
              <Detail label="Student ID" value={student ? `STD-${student.id}` : '—'} />
              <Detail label="Student name" value={student?.name ?? '—'} />
              <Detail label="Fees type" value={data.feesType?.description ?? '—'} />
              <Detail
                label="Total amount"
                value={<span className="money">{money(data.amount)}</span>}
              />

              {/* Only a monthly fee belongs to a course. */}
              {isMonthly && (
                <>
                  <Detail label="Grade" value={course?.grade?.description ?? '—'} />
                  <Detail label="Subject" value={course?.subject?.description ?? '—'} />
                  <Detail label="Group" value={course?.groupName ?? '—'} />
                  <Detail label="Month-Year" value={monthYear(data.month, data.year)} />
                </>
              )}

              <Detail label="Paid" value={<span className="money">{money(data.paidAmount)}</span>} />
              <Detail
                label="Remaining"
                value={<span className="money">{money(data.amount - data.paidAmount)}</span>}
              />
              <Detail
                label="Status"
                value={<PaidBadge paid={data.isSubmitted} partial={data.paidAmount > 0} />}
              />
            </div>

            <hr style={{ margin: 'var(--sp-5) 0', border: 0, borderTop: '1px solid var(--border)' }} />

            <SectionTitle>Fees payments</SectionTitle>

            <LocalDataTable
              rows={payments}
              rowKey={(p) => p.id}
              emptyMessage="No payments recorded."
              columns={[
                { key: 'id', header: '#', render: (p) => p.id, width: '80px' },
                {
                  key: 'when', header: 'Date',
                  render: (p) => new Date(p.createdDateTime).toLocaleString(),
                },
                {
                  key: 'amount', header: 'Paid amount', align: 'right',
                  render: (p) => <span className="money">{money(p.amount)}</span>,
                },
                // Reversing a payment is Director-only on the server; do not offer it to a Cashier.
                ...(hasRole(Roles.Director) ? [{
                  key: 'actions', header: '', align: 'right' as const,
                  render: (p: { id: number; amount: number }) => (
                    <button
                      type="button"
                      className="btn btn--sm btn--danger-quiet"
                      onClick={() => setPendingDelete(p)}
                    >
                      <IconTrash />
                      Reverse
                    </button>
                  ),
                }] : []),
              ]}
            />

            {/* The legacy table carried a Total row under the payments. */}
            {payments.length > 0 && (
              <p style={{ textAlign: 'right', marginTop: 'var(--sp-3)' }}>
                Total paid: <strong className="money">{money(totalPaid)}</strong>
              </p>
            )}

            {hasRole(Roles.Director) && isMonthly && (
              <div
                style={{
                  marginTop: 'var(--sp-5)',
                  paddingTop: 'var(--sp-4)',
                  borderTop: '1px solid var(--border)',
                }}
              >
                <button
                  type="button"
                  className="btn btn--danger"
                  disabled={!canDeleteFee || busy}
                  title={canDeleteFee ? undefined : 'Only unpaid fees can be deleted.'}
                  onClick={() => setDeletingFee(true)}
                >
                  Delete fee
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        destructive
        busy={busy}
        title="Reverse this payment?"
        confirmLabel="Reverse"
        message={pendingDelete ? (
          <>
            <p>Remove the <strong className="money">{money(pendingDelete.amount)}</strong> instalment.</p>
            <p>The fee will be reopened. The slip it belonged to is kept.</p>
          </>
        ) : null}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={deletingFee}
        destructive
        busy={busy}
        title="Delete this fees record?"
        confirmLabel="Delete"
        message={data ? (
          <>
            <p>
              Fee <strong>#{data.id}</strong>, amount{' '}
              <strong className="money">{money(data.amount)}</strong>.
            </p>
            <p>This cannot be undone.</p>
          </>
        ) : null}
        onConfirm={() => void confirmDeleteFee()}
        onCancel={() => setDeletingFee(false)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Change student fees
// ---------------------------------------------------------------------------

type EnrolledCourse = {
  id: number;
  courseId: number;
  studentCourseFees?: { amount: number };
  course?: { grade?: { description: string }; subject?: { description: string }; groupName?: string };
};

/**
 * Adjust the agreed monthly fee per course for one student — the discount screen.
 *
 * Saving also tops up the current month's fee rows for any enrolment missing one, which is
 * why the legacy enrolment flow redirects here.
 */
export function ChangeStudentFeesPage() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { run, busy } = useMutation();

  const enrolled = useResource<EnrolledCourse[]>(
    studentId ? `/api/enrollment/${studentId}/enrolled` : null);

  const [amounts, setAmounts] = useState<Record<number, string>>({});

  useEffect(() => {
    if (!enrolled.data) return;
    setAmounts(Object.fromEntries(
      enrolled.data.map((e) => [e.courseId, String(e.studentCourseFees?.amount ?? 0)])));
  }, [enrolled.data]);

  async function save() {
    const ok = await run(
      () => api.put('/api/studentfees/student-fees', {
        studentId: Number(studentId),
        courses: (enrolled.data ?? []).map((e) => ({
          courseId: e.courseId,
          feesAmount: Number(amounts[e.courseId]) || 0,
        })),
      }),
      'Student fees updated.',
    );

    if (ok) navigate('/students');
  }

  return (
    <>
      <div className="page__header">
        <div>
          <h2 className="page__title">Student fees — STD-{studentId}</h2>
          <p className="page__subtitle">
            Saving also creates this month's fee rows for any enrolment that does not have one.
          </p>
        </div>
      </div>

      <div className="card card--md">
        {enrolled.loading && <p>Loading…</p>}
        {enrolled.error && <p className="field__error">{enrolled.error}</p>}

        {enrolled.data && enrolled.data.length === 0 && (
          <p style={{ color: 'var(--text-muted)' }}>This student is not enrolled on any course.</p>
        )}

        {enrolled.data && enrolled.data.length > 0 && (
          <>
            {/* Paged for display only. `amounts` is keyed by course id and the save posts
                every enrolment, so edits on other pages are not lost. */}
            <LocalDataTable
              rows={enrolled.data}
              rowKey={(r) => r.courseId}
              emptyMessage="No enrolled courses."
              columns={[
                {
                  key: 'course', header: 'Course',
                  render: (r) => [r.course?.grade?.description, r.course?.subject?.description,
                    r.course?.groupName].filter(Boolean).join(' · '),
                },
                {
                  key: 'fee', header: 'Monthly fee', align: 'right', width: '180px',
                  render: (r) => (
                    <input
                      className="input"
                      style={{ maxWidth: 140, textAlign: 'right' }}
                      value={amounts[r.courseId] ?? ''}
                      onChange={(e) =>
                        setAmounts((current) => ({ ...current, [r.courseId]: e.target.value }))
                      }
                      inputMode="decimal"
                      aria-label={`Monthly fee for course ${r.courseId}`}
                    />
                  ),
                },
              ]}
            />

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button type="button" className="btn btn--primary" onClick={() => void save()} disabled={busy}>
                {busy ? 'Saving…' : 'Save fees'}
              </button>
              <button type="button" className="btn" onClick={() => navigate('/students')} disabled={busy}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Generate advance
// ---------------------------------------------------------------------------

export function GenerateAdvancePage() {
  const navigate = useNavigate();
  const { run, busy } = useMutation();

  const [studentId, setStudentId] = useState('');
  const [months, setMonths] = useState('1');

  async function generate() {
    const ok = await run(
      () => api.post('/api/studentfees/generate-advance', {
        studentId: Number(studentId),
        noOfMonths: Number(months),
      }),
      'Advance fees generated.',
    );

    // Straight on to collecting from this student, with them already loaded - the legacy
    // action ended with RedirectToAction("FeesSubmission", new { id = model.StudentId }).
    if (ok) navigate(`/fees/submission?studentId=${Number(studentId)}`);
  }

  return (
    <>
      <div className="page__header">
        <div>
          <h2 className="page__title">Generate Advance Fees</h2>
          <p className="page__subtitle">
            Creates fee rows ahead of time so a student can pay several months at once.
          </p>
        </div>
      </div>

      <form
        className="card card--sm"
        onSubmit={(e) => { e.preventDefault(); void generate(); }}
      >
        <NumberField id="studentId" label="Student ID" value={studentId} onChange={setStudentId} required />
        <NumberField
          id="months"
          label="Number of months"
          value={months}
          onChange={setMonths}
          hint="1 to 3. Note the current month is always included as well, so 3 produces four months in total."
        />

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button
            type="submit"
            className="btn btn--accent"
            disabled={busy || !(Number(studentId) > 0) || !(Number(months) > 0)}
          >
            {busy ? 'Generating…' : 'Generate'}
          </button>
          <button type="button" className="btn" onClick={() => navigate('/fees')} disabled={busy}>Cancel</button>
        </div>
      </form>
    </>
  );
}

// ---------------------------------------------------------------------------
// Advanced search
// ---------------------------------------------------------------------------

type SearchRow = {
  feesId: number;
  studentId: number;
  studentName: string;
  grade: string;
  subject: string;
  group: string;
  feesType: string;
  month: number | null;
  year: number | null;
  totalAmount: number;
  paidAmount: number;
  submitDateTime: string | null;
};

/**
 * A datetime-local value as UTC, to the minute: "2026-03-01T14:30" local -> "2026-03-01T09:30".
 *
 * FeesPayment.CreatedDateTime is stored in UTC but the picker hands back local time, so a
 * range typed in local hours would select the wrong payments. The legacy screen ran the same
 * conversion through moment().utc() before calling the endpoint.
 *
 * Blank stays blank, so an unset bound drops out of the query string rather than becoming
 * the epoch.
 */
function toUtcMinutes(localValue: string): string | undefined {
  if (!localValue) return undefined;

  const parsed = new Date(localValue);
  if (Number.isNaN(parsed.getTime())) return undefined;

  return parsed.toISOString().slice(0, 16);
}

/**
 * Advanced fee search.
 *
 * `status` drives two genuinely different queries on the server: true searches payments
 * that were made, false searches fees still outstanding. The totals mean different things
 * in each mode, which is why the label changes with the toggle.
 *
 * Every filter the legacy screen offered is here. The three lookups use -1 for "Any", which
 * is the sentinel the repository tests against - leaving one blank drops it from the query
 * string, and the action's own default is that same -1.
 */
export function AdvancedSearchPage() {
  const navigate = useNavigate();
  const lookups = useLookups();

  const [status, setStatus] = useState<'true' | 'false'>('true');
  const [studentIdFrom, setStudentIdFrom] = useState('');
  const [studentIdTo, setStudentIdTo] = useState('');
  const [studentName, setStudentName] = useState('');
  const [gradeId, setGradeId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [group, setGroup] = useState('');
  const [feesTypeId, setFeesTypeId] = useState('');
  const [monthYearFrom, setMonthYearFrom] = useState('');
  const [monthYearTo, setMonthYearTo] = useState('');
  const [submitDateTimeFrom, setSubmitDateTimeFrom] = useState('');
  const [submitDateTimeTo, setSubmitDateTimeTo] = useState('');

  const [results, setResults] = useState<{ feesPayments: SearchRow[]; totalAmount: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function clearFilters() {
    setStudentIdFrom('');
    setStudentIdTo('');
    setStudentName('');
    setGradeId('');
    setSubjectId('');
    setGroup('');
    setFeesTypeId('');
    setMonthYearFrom('');
    setMonthYearTo('');
    setSubmitDateTimeFrom('');
    setSubmitDateTimeTo('');
  }

  async function search() {
    setLoading(true);
    setError(null);

    try {
      const query = toQuery({
        status,
        studentIdFrom: studentIdFrom || undefined,
        studentIdTo: studentIdTo || undefined,
        studentName: studentName || undefined,
        gradeId: gradeId || undefined,
        subjectId: subjectId || undefined,
        // Matched with ==, not Contains, so an empty string would search for a course whose
        // group name is literally blank. It has to be absent, not empty.
        group: group.trim() || undefined,
        feesTypeId: feesTypeId || undefined,
        // "2024-03" from a month input; only Month and Year are read server-side.
        monthYearFrom: monthYearFrom || undefined,
        monthYearTo: monthYearTo || undefined,
        // Submission times are stored in UTC and the picker gives local time. The legacy
        // screen converted with moment().utc() before sending; this is that conversion.
        submitDateTimeFrom: toUtcMinutes(submitDateTimeFrom),
        submitDateTimeTo: toUtcMinutes(submitDateTimeTo),
      });

      setResults(await api.get(`/api/studentfees/search${query}`));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="page__header">
        <div>
          <h2 className="page__title">Advanced Fee Search</h2>
          <p className="page__subtitle">
            Submitted mode lists payments received. Outstanding mode lists fees still owed.
          </p>
        </div>
      </div>

      {/* A real form, not a div: Enter in any field has to run the search, and only a
          submit button inside a form gets that for free. */}
      <form
        className="card"
        style={{ marginBottom: 24 }}
        onSubmit={(e) => { e.preventDefault(); void search(); }}
      >
        <FormGrid>
          {/* Not a SelectField: that always renders a blank placeholder option, and a blank
              status binds to null, which the repository reads as "not submitted". Status is
              never absent here, so it must not be offerable. */}
          <div className="field">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              value={status}
              onChange={(e) => setStatus(e.target.value as 'true' | 'false')}
            >
              <option value="true">Submitted</option>
              <option value="false">Not submitted</option>
            </select>
          </div>
          <NumberField id="idFrom" label="Student ID from" value={studentIdFrom} onChange={setStudentIdFrom} />
          <NumberField id="idTo" label="Student ID to" value={studentIdTo} onChange={setStudentIdTo} />

          <TextField id="studentName" label="Student name" value={studentName} onChange={setStudentName} />
          <SelectField
            id="gradeId" label="Grade" value={gradeId} onChange={setGradeId}
            placeholder="Any" options={lookups.grades}
          />
          <SelectField
            id="subjectId" label="Subject" value={subjectId} onChange={setSubjectId}
            placeholder="Any" options={lookups.subjects}
          />

          <TextField
            id="group" label="Group" value={group} onChange={setGroup}
            hint="Exact match, not a partial one."
          />
          <SelectField
            id="feesTypeId" label="Fees type" value={feesTypeId} onChange={setFeesTypeId}
            placeholder="Any" options={lookups.feesTypes}
          />
          <div aria-hidden="true" />

          <TextField
            id="monthYearFrom" label="Month-Year from" type="month"
            value={monthYearFrom} onChange={setMonthYearFrom}
          />
          <TextField
            id="monthYearTo" label="Month-Year to" type="month"
            value={monthYearTo} onChange={setMonthYearTo}
          />
          <div aria-hidden="true" />

          <TextField
            id="submitDateTimeFrom" label="Submitted from" type="datetime-local"
            value={submitDateTimeFrom} onChange={setSubmitDateTimeFrom}
          />
          <TextField
            id="submitDateTimeTo" label="Submitted to" type="datetime-local"
            value={submitDateTimeTo} onChange={setSubmitDateTimeTo}
          />
        </FormGrid>

        <div style={{ display: 'flex', gap: 8 }}>
          <button type="submit" className="btn btn--primary" disabled={loading}>
            {loading ? 'Searching…' : 'Search'}
          </button>
          {/* Explicitly type="button": inside a form, an untyped button submits it. */}
          <button type="button" className="btn" onClick={clearFilters} disabled={loading}>
            Clear filters
          </button>
        </div>
      </form>

      {error && <p className="field__error">{error}</p>}

      {results && (
        <div className="dt">
          <div className="dt__toolbar">
            <strong>
              {status === 'true' ? 'Total collected' : 'Total outstanding'}:{' '}
              <span className="money">{money(results.totalAmount)}</span>
            </strong>
            <span style={{ color: 'var(--text-muted)' }}>{results.feesPayments.length} rows</span>
          </div>

          {/* This query is unbounded — a wide date range can return thousands of rows —
              so paging it is not cosmetic. The total in the toolbar is for the whole
              result set, not the visible page. */}
          <LocalDataTable
            rows={results.feesPayments}
            rowKey={(r) => `${r.feesId}-${r.submitDateTime ?? 'none'}-${r.paidAmount}`}
            emptyMessage="Nothing matched."
            columns={[
              { key: 'student', header: 'Student', render: (r) => `${r.studentName} (STD-${r.studentId})` },
              { key: 'type', header: 'Type', render: (r) => r.feesType },
              {
                key: 'course', header: 'Course',
                render: (r) => [r.grade, r.subject, r.group].filter(Boolean).join(' · ') || '—',
              },
              { key: 'period', header: 'Period', render: (r) => monthYear(r.month, r.year) },
              {
                key: 'total', header: 'Total', align: 'right',
                render: (r) => <span className="money">{money(r.totalAmount)}</span>,
              },
              {
                key: 'paid', header: 'Paid', align: 'right',
                render: (r) => <span className="money">{money(r.paidAmount)}</span>,
              },
              {
                key: 'submitted', header: 'Submitted',
                render: (r) => (r.submitDateTime ? new Date(r.submitDateTime).toLocaleDateString() : '—'),
              },
              {
                key: 'actions', header: '', align: 'right',
                render: (r) => (
                  <button
                    type="button"
                    className="btn btn--sm"
                    onClick={() => navigate(`/fees/details/${r.feesId}`)}
                  >
                    Details
                  </button>
                ),
              },
            ]}
          />
        </div>
      )}
    </>

  );
}
